// passkeyLoginService.ts
// ───────────────────────────────────────────────────────────────────────────
// Cliente del ingreso con passkey SIN sesión (change login-rapido-biometrico).
// Habla con las dos edges públicas core-passkey-login-options / -verify, corre
// la ceremonia del navegador (Touch ID, Face ID, Hello, huella) y, si el
// servidor verifica, instala la sesión que devuelve.
//
// Dos decisiones de diseño que importan para la PWA de Chrome:
//   - El desafío se pide ANTES del clic (pedirOpciones) y ingresarConPasskey
//     arranca la ceremonia sin ningún await de red antes: la biometría sale en
//     el primer gesto.
//   - Cada falla se clasifica en un motivo que la pantalla puede explicar, y el
//     motivo viaja con el siguiente pedido de desafío para que quede rastro en
//     el servidor (los errores de WebAuthn mueren en el navegador).
// ───────────────────────────────────────────────────────────────────────────

import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';

import { supabase } from '@/lib/supabase';

const FN_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/** El desafío del servidor vive 60 s; se considera vigente un poco menos. */
export const VIGENCIA_OPCIONES_MS = 50_000;
/** Un NotAllowedError más rápido que esto es "no hay passkey", no una cancelación. */
const UMBRAL_SIN_CREDENCIAL_MS = 800;

export type MotivoFallo =
  | 'cancelado'
  | 'sin-credencial'
  | 'no-permitido'
  | 'desafio-vencido'
  | 'limite'
  | 'otro';

export class FalloIngreso extends Error {
  readonly motivo: MotivoFallo;
  constructor(motivo: MotivoFallo, detalle?: string) {
    super(detalle ?? motivo);
    this.name = 'FalloIngreso';
    this.motivo = motivo;
  }
}

/** Lo que la pantalla le dice a la persona por cada motivo. */
export const MENSAJES_DE_FALLO: Record<MotivoFallo, { titulo: string; detalle: string }> = {
  cancelado: {
    titulo: 'Se canceló la verificación',
    detalle: 'Podés reintentar o entrar con tu contraseña.',
  },
  'sin-credencial': {
    titulo: 'Este navegador no tiene tu passkey',
    detalle:
      'Entrá una vez con tu contraseña y activá la entrada rápida en este dispositivo.',
  },
  'no-permitido': {
    titulo: 'El navegador no permitió la biometría',
    detalle:
      'Revisá que el dispositivo tenga huella, Face ID o PIN configurado. Mientras tanto, entrá con tu contraseña.',
  },
  'desafio-vencido': {
    titulo: 'Pasó demasiado tiempo',
    detalle: 'Volvé a tocar el botón para reintentar.',
  },
  limite: {
    titulo: 'Demasiados intentos',
    detalle: 'Esperá unos minutos o entrá con tu contraseña.',
  },
  otro: {
    titulo: 'No pudimos validar tu passkey',
    detalle: 'Probá de nuevo o entrá con tu contraseña.',
  },
};

export interface OpcionesDeIngreso {
  options: PublicKeyCredentialRequestOptionsJSON;
  /** Cuándo se pidieron (ms), para saber si todavía están vigentes. */
  pedidasEn: number;
}

export function opcionesVigentes(o: OpcionesDeIngreso | null, ahora = Date.now()): o is OpcionesDeIngreso {
  return !!o && ahora - o.pedidasEn < VIGENCIA_OPCIONES_MS;
}

/**
 * Clasifica un error de navigator.credentials.get() (ya envuelto por
 * SimpleWebAuthn, que conserva `name` y `code`). Chrome y Safari usan el mismo
 * NotAllowedError para "cancelaste", "se acabó el tiempo" y "no hay passkey":
 * lo único que los separa es cuánto tardó.
 */
export function clasificarErrorDeNavegador(
  err: unknown,
  tardoMs: number,
  /**
   * Hubo que pedir el desafío después del clic: entre el gesto y la biometría
   * pasó una espera de red y Safari y las PWA pueden haber perdido el gesto, lo
   * que da el MISMO NotAllowedError inmediato que «no hay passkey». Sin esta
   * salvedad, dejar el login abierto más de 50 s apagaba la entrada rápida para
   * siempre en ese navegador.
   */
  gestoPerdido = false,
): MotivoFallo {
  const e = err as { name?: string; code?: string } | null;
  if (e?.name === 'NotAllowedError') {
    if (gestoPerdido) return 'desafio-vencido';
    return tardoMs < UMBRAL_SIN_CREDENCIAL_MS ? 'sin-credencial' : 'cancelado';
  }
  if (e?.name === 'AbortError' || e?.code === 'ERROR_CEREMONY_ABORTED') return 'cancelado';
  if (
    e?.name === 'SecurityError' ||
    e?.name === 'NotSupportedError' ||
    e?.code === 'ERROR_INVALID_RP_ID' ||
    e?.code === 'ERROR_INVALID_DOMAIN'
  ) {
    return 'no-permitido';
  }
  return 'otro';
}

// ─── Llamadas a las edges (sin Authorization: todavía no hay sesión) ────────

/** Lo que devuelven las dos edges; los campos que no vienen quedan en undefined. */
interface RespuestaDeEdge {
  options?: PublicKeyCredentialRequestOptionsJSON;
  ok?: boolean;
  access_token?: string;
  refresh_token?: string;
  error?: string;
}

async function post(path: string, body: unknown): Promise<{ status: number; json: RespuestaDeEdge | null }> {
  let r: Response;
  try {
    r = await fetch(`${FN_BASE}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: ANON_KEY },
      body: JSON.stringify(body),
    });
  } catch {
    throw new FalloIngreso('otro', 'sin conexión con el servidor');
  }
  let json: RespuestaDeEdge | null = null;
  try {
    json = (await r.json()) as RespuestaDeEdge;
  } catch {
    // cuerpo vacío o no JSON
  }
  return { status: r.status, json };
}

// El motivo de la última falla viaja con el próximo pedido de desafío.
let motivoPendiente: MotivoFallo | null = null;

export function reportarFallo(motivo: MotivoFallo): void {
  motivoPendiente = motivo;
}

/** Pide el desafío para el email. Se llama al abrir el login, antes del clic. */
export async function pedirOpciones(email: string): Promise<OpcionesDeIngreso> {
  const report = motivoPendiente ? { motivo: motivoPendiente } : undefined;
  motivoPendiente = null;
  const { status, json } = await post('core-passkey-login-options', { email, report });
  if (status === 429) throw new FalloIngreso('limite');
  if (status !== 200 || !json?.options) throw new FalloIngreso('otro', `HTTP ${status}`);
  return { options: json.options as PublicKeyCredentialRequestOptionsJSON, pedidasEn: Date.now() };
}

/**
 * Corre la biometría con el desafío ya pedido y, si el servidor verifica,
 * instala la sesión. 🔴 La primera instrucción asíncrona es la ceremonia
 * (startAuthentication): no poner ningún await antes, o se pierde el gesto del
 * clic y las PWA y Safari cancelan la biometría.
 */
export async function ingresarConPasskey(
  email: string,
  opciones: OpcionesDeIngreso,
  /** El desafío se pidió después del clic (estaba vencido): el gesto pudo perderse. */
  opts: { gestoPerdido?: boolean } = {},
): Promise<void> {
  const inicio = Date.now();
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: opciones.options });
  } catch (err) {
    throw new FalloIngreso(
      clasificarErrorDeNavegador(err, Date.now() - inicio, opts.gestoPerdido),
    );
  }

  const { status, json } = await post('core-passkey-login-verify', { email, credential });
  if (status === 429) throw new FalloIngreso('limite');
  if (status !== 200 || !json?.ok) {
    const vencido = Date.now() - opciones.pedidasEn > VIGENCIA_OPCIONES_MS + 5_000;
    throw new FalloIngreso(vencido ? 'desafio-vencido' : 'otro', `HTTP ${status}`);
  }

  const { error } = await supabase.auth.setSession({
    access_token: json.access_token as string,
    refresh_token: json.refresh_token as string,
  });
  if (error) throw new FalloIngreso('otro', error.message);
}
